import React, { useState, useEffect } from 'react';
import {
    X, Building2, MapPin, Pencil, AlertTriangle, CheckCircle,
    TrendingDown, TrendingUp, Minus, Users, Clock, ShieldAlert,
    Calendar, Loader2,
} from 'lucide-react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
    apiClient, Site, SiteRiskLeaderboardEntry, SiteRiskScore, SiteRiskTrend,
    Driver, BreakdownCategoryDefinition, IssueSeverity,
} from '../../services/api';
import { iconComponents } from '../../utils/ui-mappings';
import DriverViewModal from './DriverViewModal';
import MultiSelectFilter from '../MultiSelectFilter';

type Tab = 'site' | 'drivers' | 'trends';

interface SiteViewModalProps {
    site: SiteRiskLeaderboardEntry | null;
    isOpen: boolean;
    onClose: () => void;
    onEdit?: () => void;
    breakdownCategories: BreakdownCategoryDefinition[];
}

const Row = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) => (
    <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="min-w-0">
            <p className="text-xs text-gray-500">{label}</p>
            <div className="text-sm font-medium text-gray-900 mt-0.5 break-words">{value}</div>
        </div>
    </div>
);

const getIssueBadgeClasses = (severity: IssueSeverity) => {
    switch (severity) {
        case 'critical': return { wrap: 'bg-red-50 border border-red-100',    icon: 'text-red-500',    count: 'text-red-800 font-semibold',   label: 'text-red-700'   };
        case 'warning':  return { wrap: 'bg-yellow-50 border border-yellow-100', icon: 'text-yellow-500', count: 'text-yellow-800 font-semibold', label: 'text-yellow-700' };
        default:         return { wrap: 'bg-gray-50 border border-gray-100',   icon: 'text-gray-400',   count: 'text-gray-800 font-semibold',   label: 'text-gray-600'  };
    }
};

const DRIVERS_PER_PAGE = 10;

const SiteViewModal: React.FC<SiteViewModalProps> = ({ site, isOpen, onClose, onEdit, breakdownCategories }) => {
    const [activeTab, setActiveTab] = useState<Tab>('site');

    const [siteDetail, setSiteDetail] = useState<Site | null>(null);
    const [riskScore, setRiskScore] = useState<SiteRiskScore | null>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);

    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [driversTotal, setDriversTotal] = useState(0);
    const [driversPage, setDriversPage] = useState(1);
    const [isLoadingDrivers, setIsLoadingDrivers] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);

    const [trendData, setTrendData] = useState<SiteRiskTrend | null>(null);
    const [isLoadingTrend, setIsLoadingTrend] = useState(false);
    const [trendDays, setTrendDays] = useState(30);

    useEffect(() => {
        if (isOpen && site) {
            setActiveTab('site');
            setSiteDetail(null);
            setRiskScore(null);
            setDrivers([]);
            setDriversPage(1);
            setTrendData(null);
            setSelectedDriver(null);
            loadSiteDetail();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, site?.siteId]);

    useEffect(() => {
        if (isOpen && site && activeTab === 'drivers') {
            loadDrivers(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, site?.siteId, activeTab]);

    useEffect(() => {
        if (isOpen && site && activeTab === 'trends') {
            loadTrend();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, site?.siteId, activeTab, trendDays]);

    const loadSiteDetail = async () => {
        if (!site) return;
        setIsLoadingDetail(true);
        try {
            const [detailRes, riskRes] = await Promise.all([
                apiClient.getSite(site.siteId),
                apiClient.getSiteRiskScore(site.siteId),
            ]);
            setSiteDetail(detailRes.data);
            setRiskScore(riskRes.data);
        } catch (err) {
            console.error('Failed to load site detail:', err);
        } finally {
            setIsLoadingDetail(false);
        }
    };

    const loadDrivers = async (page: number) => {
        if (!site) return;
        setIsLoadingDrivers(true);
        try {
            const res = await apiClient.getSiteDrivers(site.siteId, { page, pageSize: DRIVERS_PER_PAGE });
            setDrivers(res.data);
            setDriversTotal(res.totalCount);
            setDriversPage(page);
        } catch (err) {
            console.error('Failed to load site drivers:', err);
        } finally {
            setIsLoadingDrivers(false);
        }
    };

    const loadTrend = async () => {
        if (!site) return;
        setIsLoadingTrend(true);
        try {
            const res = await apiClient.getSiteRiskTrend(site.siteId, trendDays);
            setTrendData(res.data);
        } catch (err) {
            console.error('Failed to load site trend:', err);
        } finally {
            setIsLoadingTrend(false);
        }
    };

    const handleClose = () => {
        setActiveTab('site');
        setSiteDetail(null);
        setRiskScore(null);
        setDrivers([]);
        setTrendData(null);
        setSelectedDriver(null);
        onClose();
    };

    if (!isOpen || !site) return null;

    const cardClass = 'bg-gray-50/80 rounded-lg border border-gray-100 p-4';

    const getRiskBadgeClass = (risk?: string) => {
        switch (risk) {
            case 'high':   return 'bg-red-100 text-red-800 border-red-200';
            case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'low':    return 'bg-green-100 text-green-800 border-green-200';
            default:       return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getRiskColor = (level: string) => {
        switch (level) {
            case 'low':    return '#69BE28';
            case 'medium': return '#f59e0b';
            case 'high':   return '#ef4444';
            default:       return '#6b7280';
        }
    };

    const getTrendIcon = (trend: string) => {
        switch (trend) {
            case 'improving': return <TrendingDown className="w-4 h-4 text-green-600" />;
            case 'worsening': return <TrendingUp className="w-4 h-4 text-red-600" />;
            default:          return <Minus className="w-4 h-4 text-gray-400" />;
        }
    };

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return 'Not provided';
        return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    };

    const formatShortDate = (dateString: string) => {
        const d = new Date(dateString);
        return `${d.getDate()}/${d.getMonth() + 1}`;
    };

    const tabs: { id: Tab; label: string }[] = [
        { id: 'site',    label: 'Site'    },
        { id: 'drivers', label: 'Drivers' },
        { id: 'trends',  label: 'Trends'  },
    ];

    const totalDriverPages = Math.ceil(driversTotal / DRIVERS_PER_PAGE);

    return (
        <>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full flex flex-col" style={{ height: '60vh' }}>

                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                        <div className="flex items-center gap-4 min-w-0 flex-1">
                            <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(105,190,40,0.1)' }}>
                                <Building2 className="w-5 h-5" style={{ color: '#69BE28' }} />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-base font-semibold text-gray-900 truncate">{site.siteName}</h2>
                                <p className="text-xs text-gray-500 font-mono">{site.siteCode}</p>
                                <div className="flex items-center gap-2 mt-1.5">
                                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${getRiskBadgeClass(site.riskLevel)}`}>
                                        {site.riskLevel.charAt(0).toUpperCase() + site.riskLevel.slice(1)} Risk
                                    </span>
                                    {siteDetail && (
                                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${siteDetail.active ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}>
                                            {siteDetail.active ? 'Active' : 'Inactive'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                            {onEdit && (
                                <button
                                    onClick={onEdit}
                                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                                    title="Edit Site"
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                            )}
                            <button
                                onClick={handleClose}
                                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Tab bar */}
                    <div className="flex border-b border-gray-200 px-6 flex-shrink-0">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-3 py-3 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
                                    activeTab === tab.id
                                        ? 'border-green-500 text-green-700'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                                style={activeTab === tab.id ? { borderColor: '#69BE28', color: '#4a8a1c' } : {}}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Tab content - scrollable */}
                    <div className="flex-1 overflow-y-auto">

                        {/* ── Site ── */}
                        {activeTab === 'site' && (
                            <div className="p-6">
                                {isLoadingDetail ? (
                                    <div className="flex items-center justify-center h-48">
                                        <Loader2 className="w-5 h-5 animate-spin mr-2 text-gray-400" />
                                        <span className="text-sm text-gray-500">Loading site details...</span>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                                        {/* Details */}
                                        <div className={`${cardClass} space-y-3`}>
                                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Details</h3>
                                            <Row
                                                icon={<Building2 className="w-4 h-4 text-gray-400" />}
                                                label="Site Code"
                                                value={<span className="font-mono bg-white border border-gray-200 rounded px-1.5 py-0.5 text-xs">{site.siteCode}</span>}
                                            />
                                            {siteDetail && (
                                                <>
                                                    <Row
                                                        icon={<MapPin className="w-4 h-4 text-gray-400" />}
                                                        label="Address"
                                                        value={
                                                            siteDetail.addressLine1 ? (
                                                                <div>
                                                                    <p>{siteDetail.addressLine1}</p>
                                                                    {siteDetail.addressLine2 && <p>{siteDetail.addressLine2}</p>}
                                                                    {(siteDetail.city || siteDetail.postcode) && (
                                                                        <p>{[siteDetail.city, siteDetail.postcode].filter(Boolean).join(', ')}</p>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400 italic text-sm font-normal">No address provided</span>
                                                            )
                                                        }
                                                    />
                                                    <Row icon={<Calendar className="w-4 h-4 text-gray-400" />} label="Created" value={formatDate(siteDetail.createdAt)} />
                                                </>
                                            )}
                                        </div>

                                        {/* Risk Score */}
                                        <div className={`${cardClass} space-y-3`}>
                                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Risk Score</h3>
                                            <Row
                                                icon={<ShieldAlert className="w-4 h-4 text-gray-400" />}
                                                label="Current Score"
                                                value={
                                                    <span
                                                        className="inline-block px-2.5 py-0.5 rounded text-sm font-bold text-white"
                                                        style={{ backgroundColor: getRiskColor(site.riskLevel) }}
                                                    >
                                                        {site.totalScore.toFixed(0)}
                                                    </span>
                                                }
                                            />
                                            <Row
                                                icon={<div className="w-4 h-4 flex items-center">{getTrendIcon(site.trend)}</div>}
                                                label="Trend"
                                                value={
                                                    <span className="capitalize">
                                                        {site.trend}
                                                        {site.scoreChange != null && site.scoreChange !== 0 && (
                                                            <span className={`ml-1.5 text-xs font-medium ${site.scoreChange > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                                ({site.scoreChange > 0 ? '+' : ''}{site.scoreChange.toFixed(1)})
                                                            </span>
                                                        )}
                                                    </span>
                                                }
                                            />
                                            <Row icon={<Users className="w-4 h-4 text-gray-400" />} label="Total Drivers" value={site.totalDrivers} />
                                            {riskScore && (
                                                <>
                                                    <Row icon={<CheckCircle className="w-4 h-4 text-green-500" />} label="With Recent Checks" value={riskScore.driversWithRecentChecks} />
                                                    <Row icon={<Clock className="w-4 h-4 text-amber-500" />} label="Overdue Recheck" value={riskScore.driversOverdue} />
                                                    <Row icon={<AlertTriangle className="w-4 h-4 text-gray-400" />} label="Never Checked" value={riskScore.driversNeverChecked} />
                                                </>
                                            )}
                                        </div>

                                        {/* Issues Breakdown */}
                                        {breakdownCategories.length > 0 && (() => {
                                            const issues = breakdownCategories
                                                .map(cat => ({ ...cat, count: site.breakdown[cat.key] || 0 }))
                                                .filter(cat => cat.count > 0);

                                            return (
                                                <div className={`${cardClass} lg:col-span-2`}>
                                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Issues Breakdown</h3>
                                                    {issues.length === 0 ? (
                                                        <div className="flex items-center gap-2 text-green-700">
                                                            <CheckCircle className="w-4 h-4 text-green-500" />
                                                            <span className="text-sm">No issues detected at this site.</span>
                                                        </div>
                                                    ) : (
                                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                            {issues.map(issue => {
                                                                const IconComponent = iconComponents[issue.icon] || AlertTriangle;
                                                                const cls = getIssueBadgeClasses(issue.severity);
                                                                return (
                                                                    <div key={issue.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${cls.wrap}`}>
                                                                        <IconComponent className={`w-3.5 h-3.5 flex-shrink-0 ${cls.icon}`} />
                                                                        <span className={cls.count}>{issue.count}</span>
                                                                        <span className={`text-xs ${cls.label}`}>{issue.label}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}

                                        {/* Inactive warning */}
                                        {siteDetail && !siteDetail.active && (
                                            <div className="lg:col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                                                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <p className="text-sm font-medium text-amber-800">Site Inactive</p>
                                                    <p className="text-xs text-amber-700 mt-0.5">This site is inactive and cannot be assigned to new drivers.</p>
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Drivers ── */}
                        {activeTab === 'drivers' && (
                            <div className="p-6 space-y-4">
                                {isLoadingDrivers && drivers.length === 0 ? (
                                    <div className="flex items-center justify-center h-48">
                                        <Loader2 className="w-5 h-5 animate-spin mr-2 text-gray-400" />
                                        <span className="text-sm text-gray-500">Loading drivers...</span>
                                    </div>
                                ) : drivers.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-400">
                                        <Users className="w-8 h-8" />
                                        <p className="text-sm">No drivers assigned to this site.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-xs text-gray-500 text-right">
                                            {driversTotal} driver{driversTotal !== 1 ? 's' : ''}
                                        </div>
                                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                            <table className="w-full text-sm">
                                                <thead className="border-b border-gray-100 bg-gray-50/95">
                                                    <tr className="text-left">
                                                        <th className="px-4 py-3.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                                                        <th className="px-4 py-3.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Risk</th>
                                                        <th className="px-4 py-3.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Last Check</th>
                                                        <th className="px-4 py-3.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Consent</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {drivers.map(driver => (
                                                        <tr
                                                            key={driver.id}
                                                            onClick={() => setSelectedDriver(driver)}
                                                            className="hover:bg-gray-50/60 transition-colors cursor-pointer"
                                                        >
                                                            <td className="px-4 py-3.5 font-medium text-gray-900">
                                                                {driver.firstName} {driver.lastName}
                                                            </td>
                                                            <td className="px-4 py-3.5">
                                                                <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                                    <span className={`h-4 w-[3px] rounded-full ${driver.riskLevel === 'high' ? 'bg-red-500' : driver.riskLevel === 'medium' ? 'bg-amber-400' : driver.riskLevel === 'low' ? 'bg-green-500' : 'bg-gray-300'}`} aria-hidden="true" />
                                                                    {driver.riskLevel ? driver.riskLevel.charAt(0).toUpperCase() + driver.riskLevel.slice(1) : 'Unknown'}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3.5 text-gray-500 text-xs">
                                                                {driver.lastLicenceCheck ? formatDate(driver.lastLicenceCheck) : 'Never'}
                                                            </td>
                                                            <td className="px-4 py-3.5">
                                                                <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                                    <span className={`h-4 w-[3px] rounded-full ${driver.consentProvided ? 'bg-green-500' : 'bg-gray-300'}`} aria-hidden="true" />
                                                                    {driver.consentProvided ? 'Given' : 'Pending'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {totalDriverPages > 1 && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-gray-500">Page {driversPage} of {totalDriverPages}</span>
                                                <div className="flex gap-2">
                                                    <button
                                                        disabled={driversPage === 1 || isLoadingDrivers}
                                                        onClick={() => loadDrivers(driversPage - 1)}
                                                        className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                                                    >
                                                        Previous
                                                    </button>
                                                    <button
                                                        disabled={driversPage === totalDriverPages || isLoadingDrivers}
                                                        onClick={() => loadDrivers(driversPage + 1)}
                                                        className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* ── Trends ── */}
                        {activeTab === 'trends' && (
                            <div className="p-6 space-y-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500">Period:</span>
                                    <div className="w-44">
                                        <MultiSelectFilter
                                            label="Trend period"
                                            allLabel="Last 30 days"
                                            options={[
                                                { value: '30', label: 'Last 30 days' },
                                                { value: '60', label: 'Last 60 days' },
                                                { value: '90', label: 'Last 90 days' },
                                            ]}
                                            values={[String(trendDays)]}
                                            onApply={(values) => setTrendDays(Number(values[values.length - 1] ?? 30))}
                                        />
                                    </div>
                                </div>

                                {isLoadingTrend ? (
                                    <div className="flex items-center justify-center h-48">
                                        <Loader2 className="w-5 h-5 animate-spin mr-2 text-gray-400" />
                                        <span className="text-sm text-gray-500">Loading trend data...</span>
                                    </div>
                                ) : !trendData || trendData.history.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-400">
                                        <AlertTriangle className="w-8 h-8" />
                                        <p className="text-sm">No trend data available for this period.</p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Score chart */}
                                        <div className={cardClass}>
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Risk Score Over Time</h3>
                                                <span className="text-xs text-gray-400">Lower is better</span>
                                            </div>
                                            <ResponsiveContainer width="100%" height={180}>
                                                <AreaChart data={trendData.history} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
                                                    <defs>
                                                        <linearGradient id="siteViewScoreGradient" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#69BE28" stopOpacity={0.15} />
                                                            <stop offset="95%" stopColor="#69BE28" stopOpacity={0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                    <XAxis
                                                        dataKey="date"
                                                        tick={{ fontSize: 10, fill: '#9ca3af' }}
                                                        tickFormatter={formatShortDate}
                                                        tickLine={false}
                                                        axisLine={false}
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 10, fill: '#9ca3af' }}
                                                        tickLine={false}
                                                        axisLine={false}
                                                        domain={['auto', 'auto']}
                                                    />
                                                    <Tooltip
                                                        contentStyle={{ fontSize: '12px', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                                        labelFormatter={(v) => formatDate(v as string)}
                                                        formatter={(value: number | undefined) => [value != null ? value.toFixed(1) : '-', 'Risk Score']}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="totalScore"
                                                        stroke="#69BE28"
                                                        strokeWidth={2}
                                                        fill="url(#siteViewScoreGradient)"
                                                        dot={false}
                                                        activeDot={{ r: 4 }}
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>

                                        {/* Summary stats */}
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                            {[
                                                {
                                                    label: 'Current Score',
                                                    value: trendData.currentScore.toFixed(1),
                                                    color: getRiskColor(site.riskLevel),
                                                },
                                                {
                                                    label: 'Previous Score',
                                                    value: trendData.previousScore != null ? trendData.previousScore.toFixed(1) : '-',
                                                    color: '#6b7280',
                                                },
                                                {
                                                    label: 'Change',
                                                    value: trendData.previousScore != null
                                                        ? `${(trendData.currentScore - trendData.previousScore) > 0 ? '+' : ''}${(trendData.currentScore - trendData.previousScore).toFixed(1)}`
                                                        : '-',
                                                    color: trendData.previousScore != null
                                                        ? trendData.currentScore < trendData.previousScore ? '#69BE28' : '#ef4444'
                                                        : '#6b7280',
                                                },
                                                {
                                                    label: 'Trend',
                                                    value: trendData.trend.charAt(0).toUpperCase() + trendData.trend.slice(1),
                                                    color: trendData.trend === 'improving' ? '#69BE28' : trendData.trend === 'worsening' ? '#ef4444' : '#6b7280',
                                                },
                                            ].map(stat => (
                                                <div key={stat.label} className={cardClass}>
                                                    <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                                                    <p className="text-base font-bold" style={{ color: stat.color }}>{stat.value}</p>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Driver compliance snapshot (latest data point) */}
                                        {(() => {
                                            const latest = trendData.history[trendData.history.length - 1];
                                            return (
                                                <div className={`${cardClass} grid grid-cols-3 gap-4 text-center`}>
                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-1">Recent Checks</p>
                                                        <p className="text-lg font-bold text-green-600">{latest.driversWithRecentChecks}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-1">Overdue</p>
                                                        <p className="text-lg font-bold text-amber-500">{latest.driversOverdue}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-1">Never Checked</p>
                                                        <p className="text-lg font-bold text-gray-500">{latest.driversNeverChecked}</p>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </>
                                )}
                            </div>
                        )}

                    </div>
                </div>
            </div>

            {/* Nested DriverViewModal - opens when a driver row is clicked */}
            <DriverViewModal
                driver={selectedDriver}
                isOpen={!!selectedDriver}
                onClose={() => setSelectedDriver(null)}
            />
        </>
    );
};

export default SiteViewModal;
